// src/pages/school/Teachers.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Container, Row, Col, Card, Table, Button, ButtonGroup,
  Form, InputGroup, Modal, FloatingLabel, Badge, Spinner, ProgressBar,Dropdown 
} from "react-bootstrap";
import dayjs from "dayjs";
import Swal from "sweetalert2";
import * as XLSX from "xlsx";
import {
  collection, addDoc, updateDoc, deleteDoc, doc,
  onSnapshot, serverTimestamp, query, orderBy, setDoc
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { db, storage } from "../../services/firebase";
import useSchool from "../../hooks/useSchool";

import { getApps, initializeApp } from "firebase/app";
import {
  getAuth,
  createUserWithEmailAndPassword,
  updateProfile,
  sendPasswordResetEmail,
  signOut,
} from "firebase/auth";

/* ---------------- helpers ---------------- */
const toDate = (v) => (v?.toDate ? v.toDate() : v ? new Date(v) : null);
const fmt = (d, f = "DD MMM YYYY") =>
  d && dayjs(d).isValid() ? dayjs(d).format(f) : "—";
const initials = (name = "") =>
  name.trim().split(/\s+/, 2).map(p => p[0]?.toUpperCase() || "").join("");

const randomPass = (len = 12) =>
  Array.from(crypto.getRandomValues(new Uint32Array(len)))
    .map((n) => "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789@#$%".charAt(n % 65))
    .join("");

const up = (s) => String(s || "").trim().toUpperCase();

/* Create Auth user on a secondary app so current session doesn’t get clobbered */
async function createTeacherAuthUser({ email, password, displayName }) {
  const apps = getApps();
  const primary = apps[0];
  const options = primary?.options;

  let secondaryAuth;
  if (options) {
    const secondaryApp =
      apps.find((a) => a.name === "secondary") || initializeApp(options, "secondary");
    secondaryAuth = getAuth(secondaryApp);
  } else {
    secondaryAuth = getAuth();
  }

  const cred = await createUserWithEmailAndPassword(secondaryAuth, email, password);
  try { if (displayName) await updateProfile(cred.user, { displayName }); } catch {}
  try { await signOut(secondaryAuth); } catch {}
  return cred.user; // { uid, email }
}

/* ================= Form Modal (single) ================= */

const DEFAULT = {
  name: "", employeeId: "", primarySubject: "", otherSubjects: "",
  gender: "", dob: "", phone: "", email: "", address: "", status: "active",
  joinDate: "", photoFile: null, removePhoto: false,

  // Login (stored helper fields)
  teacherLoginId: "",
  accountPassword: "",

  // Mapping (multi)
  classIds: [],         // array of class names
  sectionIds: [],       // array of section KEYS (e.g., "A", "B")
  branchIds: [],        // array of branch KEYS (e.g., "PCM", "SCIENCE")

  // Legacy fallbacks (kept in form only to seed arrays)
  className: "",
  section: "",
  branch: "",
};

/**
 * classesSchema: Map-like object:
 * {
 *   [className: string]: {
 *     sections: Set<string>,
 *     branches: Set<string>
 *   }
 * }
 */
function TeacherFormModal({ show, onHide, initial, onSave, classesSchema = {} }) {
  const [form, setForm] = useState(DEFAULT);
  const [saving, setSaving] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (initial) {
      // Seed arrays from initial arrays or legacy singles
      const seededClassIds = Array.isArray(initial.classIds) && initial.classIds.length
        ? initial.classIds
        : (initial.className ? [initial.className] : []);
      const seededSectionIds = Array.isArray(initial.sectionIds) && initial.sectionIds.length
        ? initial.sectionIds.map(up)
        : (initial.section ? [up(initial.section)] : []);
      const seededBranchIds = Array.isArray(initial.branchIds) && initial.branchIds.length
        ? initial.branchIds.map(up)
        : (initial.branch ? [up(initial.branch)] : []);

      setForm({
        name: initial.name || "",
        employeeId: initial.employeeId || "",
        primarySubject: initial.primarySubject || "",
        otherSubjects: initial.otherSubjects || "",
        gender: initial.gender || "",
        dob: initial.dob ? dayjs(toDate(initial.dob)).format("YYYY-MM-DD") : "",
        phone: initial.phone || "",
        email: initial.email || "",
        address: initial.address || "",
        joinDate: initial.joinDate ? dayjs(toDate(initial.joinDate)).format("YYYY-MM-DD") : "",
        status: initial.status || "inactive",
        photoFile: null,
        removePhoto: false,

        teacherLoginId: initial.teacherLoginId || "",
        accountPassword: "",

        classIds: seededClassIds,
        sectionIds: seededSectionIds,
        branchIds: seededBranchIds,

        className: initial.className || "",
        section: initial.section || "",
        branch: initial.branch || "",
      });
    } else {
      setForm(DEFAULT);
    }
  }, [initial, show]);

  const change = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const classNames = useMemo(() => {
    const list = Object.keys(classesSchema || {});
    return list.sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
  }, [classesSchema]);

  const sectionOptions = useMemo(() => {
    const secs = new Set();
    (form.classIds || []).forEach((c) => {
      const entry = classesSchema[c];
      (entry?.sections || new Set()).forEach((s) => secs.add(up(s)));
    });
    return Array.from(secs).sort();
  }, [classesSchema, form.classIds]);

  const branchOptions = useMemo(() => {
    const brs = new Set();
    (form.classIds || []).forEach((c) => {
      const entry = classesSchema[c];
      (entry?.branches || new Set()).forEach((b) => brs.add(up(b)));
    });
    // Always allow blank (no branch) as an option if no branches defined in schema
    return Array.from(brs).sort();
  }, [classesSchema, form.classIds]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return Swal.fire({ icon: "warning", title: "Name is required" });

    if (form.accountPassword && form.accountPassword.length < 6) {
      return Swal.fire({ icon: "warning", title: "Password must be at least 6 characters" });
    }

    // Clean arrays
    const classIds = Array.from(new Set((form.classIds || []).map((c) => String(c).trim()).filter(Boolean)));
    const sectionIds = Array.from(new Set((form.sectionIds || []).map(up).filter(Boolean)));
    const branchIds = Array.from(new Set((form.branchIds || []).map(up).filter(Boolean)));

    setSaving(true);
    try {
      await onSave({
        name: form.name.trim(),
        employeeId: String(form.employeeId || "").trim(),
        primarySubject: String(form.primarySubject || "").trim(),
        otherSubjects: String(form.otherSubjects || "").trim(),
        gender: String(form.gender || "").trim(),
        dob: form.dob || null,
        phone: String(form.phone || "").trim(),
        email: String(form.email || "").trim().toLowerCase() || null,
        address: String(form.address || "").trim(),
        joinDate: form.joinDate || null,
        status: form.status || "active",
        photoFile: form.photoFile || null,
        removePhoto: !!form.removePhoto,

        teacherLoginId: String(form.teacherLoginId || "").trim(),
        accountPassword: String(form.accountPassword || "").trim(),

        // New arrays
        classIds,
        sectionIds,
        branchIds,

        // Firsts for legacy single fields
        className: classIds[0] || "",
        section: sectionIds[0] || "",
        branch: branchIds[0] || "",
      });
      onHide?.();
    } finally { setSaving(false); }
  };

  const previewUrl = form.photoFile ? URL.createObjectURL(form.photoFile) : (initial?.photoURL || "");
  const acceptFile = (file) => {
    if (!file) return;
    if (!/image\/(png|jpeg|webp)/.test(file.type)) {
      Swal.fire({ icon: "info", title: "Please choose PNG/JPG/WEBP" });
      return;
    }
    change("photoFile", file);
    change("removePhoto", false);
  };

  return (
   <Modal show={show} onHide={onHide} centered size="lg" className="modal-elevated">
  <Form onSubmit={submit}>
    <Modal.Header closeButton className="border-0 pb-0">
      <Modal.Title className="h5 d-flex align-items-center gap-2">
        <i className="bi bi-person-badge" />
        {initial ? "Edit Teacher" : "Add Teacher"}
      </Modal.Title>
    </Modal.Header>

    <Modal.Body className="pt-3">
      <Row className="g-3">
        {/* LEFT — FORM */}
        <Col lg={8}>
          {/* Identity */}
          <div className="section">
            <div className="section-title">
              <i className="bi bi-person-lines-fill me-2" />
              Identity
            </div>
            <Row className="g-3">
              <Col md={6}>
                <FloatingLabel label="Full Name *">
                  <Form.Control
                    size="sm"
                    value={form.name}
                    onChange={(e) => change("name", e.target.value)}
                    placeholder="Teacher name"
                    required
                    autoComplete="name"
                  />
                </FloatingLabel>
              </Col>
              <Col md={6}>
                <FloatingLabel label="Employee ID">
                  <Form.Control
                    size="sm"
                    value={form.employeeId}
                    onChange={(e) => change("employeeId", e.target.value)}
                    placeholder="EMP123"
                  />
                </FloatingLabel>
              </Col>

              <Col md={6}>
                <FloatingLabel label="Teacher Login ID (optional)">
                  <Form.Control
                    size="sm"
                    value={form.teacherLoginId}
                    onChange={(e) => change("teacherLoginId", e.target.value)}
                    placeholder="e.g., TCH1001"
                    autoComplete="username"
                  />
                </FloatingLabel>
              </Col>

              <Col md={6}>
                <FloatingLabel >
                  <InputGroup size="sm">
                    <span className="input-group-text"><i className="bi bi-shield-lock" /></span>
                    <Form.Control
                      type={showPw ? "text" : "password"}
                      value={form.accountPassword}
                      onChange={(e) => change("accountPassword", e.target.value)}
                      placeholder="Min 6 characters"
                      autoComplete="new-password"
                      minLength={6}
                    />
                    <Button
                      variant="outline-secondary"
                      type="button"
                      onClick={() => setShowPw((s) => !s)}
                      title={showPw ? "Hide" : "Show"}
                    >
                      <i className={`bi ${showPw ? "bi-eye-slash" : "bi-eye"}`} />
                    </Button>
                  </InputGroup>
                </FloatingLabel>
                <div className="form-hint">
                  Leave blank to auto-generate a temporary password.
                  Prefer using <em>Send Password Reset Email</em>.
                </div>
              </Col>
            </Row>
          </div>

          {/* Teaching map */}
          <div className="section">
            <div className="section-title">
              <i className="bi bi-easel2 me-2" />
              Teaching Map
            </div>
            <Row className="g-3">
              <Col md={12}>
                <FloatingLabel label="Classes (multi-select)">
                  <Form.Select
                    size="sm"
                    multiple
                    value={form.classIds}
                    onChange={(e) => {
                      const opts = Array.from(e.target.selectedOptions).map((o) => o.value);
                      const nextSections = new Set();
                      const nextBranches = new Set();
                      opts.forEach((c) => {
                        const entry = classesSchema[c];
                        (entry?.sections || new Set()).forEach((s) => nextSections.add(up(s)));
                        (entry?.branches || new Set()).forEach((b) => nextBranches.add(up(b)));
                      });
                      const filteredSectionIds = (form.sectionIds || []).map(up).filter((s) => nextSections.has(s));
                      const filteredBranchIds = (form.branchIds || []).map(up).filter((b) => nextBranches.has(b));
                      setForm((f) => ({
                        ...f,
                        classIds: opts,
                        sectionIds: filteredSectionIds,
                        branchIds: filteredBranchIds
                      }));
                    }}
                    style={{ minHeight: 100 }}
                  >
                    {classNames.map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </Form.Select>
                </FloatingLabel>
                <div className="form-hint">Hold Ctrl/Cmd to select multiple.</div>
              </Col>

              <Col md={6}>
                <FloatingLabel label="Sections (multi-select)">
                  <Form.Select
                    size="sm"
                    multiple
                    value={form.sectionIds}
                    onChange={(e) =>
                      setForm((f) => ({
                        ...f,
                        sectionIds: Array.from(e.target.selectedOptions).map((o) => up(o.value))
                      }))
                    }
                    disabled={!form.classIds?.length}
                    style={{ minHeight: 100 }}
                  >
                    {sectionOptions.map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </Form.Select>
                </FloatingLabel>
              </Col>

              <Col md={6}>
                <FloatingLabel label="Branches (multi-select)">
                  <Form.Select
                    size="sm"
                    multiple
                    value={form.branchIds}
                    onChange={(e) =>
                      setForm((f) => ({
                        ...f,
                        branchIds: Array.from(e.target.selectedOptions).map((o) => up(o.value))
                      }))
                    }
                    disabled={!form.classIds?.length}
                    style={{ minHeight: 100 }}
                  >
                    {branchOptions.length === 0 ? (
                      <option value="">(No branch configured)</option>
                    ) : (
                      branchOptions.map((b) => <option key={b} value={b}>{b}</option>)
                    )}
                  </Form.Select>
                </FloatingLabel>
              </Col>
            </Row>
          </div>

          {/* Meta */}
          <div className="section">
            <div className="section-title">
              <i className="bi bi-card-checklist me-2" />
              Meta & Contact
            </div>
            <Row className="g-3">
              <Col md={4}>
                <FloatingLabel label="Status">
                  <Form.Select
                    size="sm"
                    value={form.status ?? "inactive"}
                    onChange={(e) => change("status", e.target.value)}
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </Form.Select>
                </FloatingLabel>
              </Col>

              <Col md={4}>
                <FloatingLabel label="Primary Subject">
                  <Form.Control
                    size="sm"
                    value={form.primarySubject}
                    onChange={(e) => change("primarySubject", e.target.value)}
                    placeholder="Physics"
                  />
                </FloatingLabel>
              </Col>

              <Col md={4}>
                <FloatingLabel label="Other Subjects (comma separated)">
                  <Form.Control
                    size="sm"
                    value={form.otherSubjects}
                    onChange={(e) => change("otherSubjects", e.target.value)}
                    placeholder="Chemistry, Biology"
                  />
                </FloatingLabel>
              </Col>

              <Col md={4}>
                <FloatingLabel label="Gender">
                  <Form.Select
                    size="sm"
                    value={form.gender}
                    onChange={(e) => change("gender", e.target.value)}
                  >
                    <option value="">—</option>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                    <option value="other">Other</option>
                  </Form.Select>
                </FloatingLabel>
              </Col>

              <Col md={4}>
                <FloatingLabel label="DOB">
                  <Form.Control
                    size="sm"
                    type="date"
                    value={form.dob}
                    onChange={(e) => change("dob", e.target.value)}
                    max={new Date().toISOString().slice(0,10)}
                  />
                </FloatingLabel>
              </Col>

              <Col md={4}>
                <FloatingLabel label="Phone">
                  <Form.Control
                    size="sm"
                    value={form.phone}
                    onChange={(e) => change("phone", e.target.value)}
                    placeholder="+91 90000 00000"
                    inputMode="tel"
                    pattern="[0-9]{10,}"
                  />
                </FloatingLabel>
              </Col>

              <Col md={8}>
                <FloatingLabel label="Email">
                  <Form.Control
                    size="sm"
                    type="email"
                    value={form.email}
                    onChange={(e) => change("email", e.target.value)}
                    placeholder="teacher@school.com"
                    autoComplete="email"
                  />
                </FloatingLabel>
              </Col>

              <Col md={4}>
                <FloatingLabel label="Join Date">
                  <Form.Control
                    size="sm"
                    type="date"
                    value={form.joinDate}
                    onChange={(e) => change("joinDate", e.target.value)}
                  />
                </FloatingLabel>
              </Col>

              <Col md={12}>
                <FloatingLabel label="Address">
                  <Form.Control
                    as="textarea"
                    size="sm"
                    style={{ height: 88 }}
                    value={form.address}
                    onChange={(e) => change("address", e.target.value)}
                    placeholder="Street, City"
                  />
                </FloatingLabel>
              </Col>
            </Row>
          </div>
        </Col>

        {/* RIGHT — AVATAR */}
        <Col lg={4}>
          <div
            className={`avatar-panel rounded-3 p-3 text-center ${dragOver ? "drag" : ""}`}
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => { e.preventDefault(); setDragOver(false); acceptFile(e.dataTransfer.files?.[0]); }}
          >
            <div className="fw-semibold mb-2">Profile Photo</div>
            <div className="d-flex justify-content-center mb-2">
              {previewUrl ? (
                <img
                  src={previewUrl}
                  alt="Preview"
                  className="avatar-lg"
                />
              ) : (
                <div className="avatar-lg placeholder">
                  <i className="bi bi-person" />
                </div>
              )}
            </div>

            <div className="d-grid gap-2">
              <Button variant="outline-primary" size="sm" type="button" onClick={() => fileRef.current?.click()}>
                <i className="bi bi-upload me-1" />
                Choose Image
              </Button>
              {previewUrl && (
                <Button variant="outline-secondary" size="sm" type="button" onClick={() => { setForm((f) => ({ ...f, photoFile: null })); }}>
                  <i className="bi bi-x-circle me-1" />
                  Clear Selection
                </Button>
              )}
              {initial?.photoURL && !form.photoFile && (
                <Form.Check
                  className="mt-1 text-start"
                  type="switch"
                  id="remove-photo"
                  label="Remove existing photo"
                  checked={!!form.removePhoto}
                  onChange={(e) => change("removePhoto", e.target.checked)}
                />
              )}
            </div>

            <input
              ref={fileRef}
              type="file"
              hidden
              accept="image/png,image/jpeg,image/webp"
              onChange={(e) => acceptFile(e.target.files?.[0] || null)}
            />
            <div className="form-hint mt-2">PNG/JPG/WEBP • up to ~2MB</div>
          </div>
        </Col>
      </Row>
    </Modal.Body>

    <Modal.Footer className="border-0 sticky-footer">
      <Button variant="outline-secondary" onClick={onHide} disabled={saving}>
        Cancel
      </Button>
      <Button type="submit" disabled={saving}>
        {saving ? "Saving…" : "Save"}
      </Button>
    </Modal.Footer>
  </Form>
</Modal>

  );
}

/* ================= Bulk upload modal ================= */

function BulkUploadModal({ show, onHide, onImport }) {
  const [rows, setRows] = useState([]); // parsed rows
  const [loading, setLoading] = useState(false);
  const [autoCreateAccounts, setAutoCreateAccounts] = useState(true);

  useEffect(() => { if (!show) setRows([]); }, [show]);

  const parseFile = async (file) => {
    setLoading(true);
    try {
      const data = await file.arrayBuffer();
      const wb = XLSX.read(data, { type: "array" });
      const ws = wb.Sheets[wb.SheetNames[0]];
      const json = XLSX.utils.sheet_to_json(ws, { defval: "" });

      const norm = (k) => String(k || "").trim().toLowerCase();
      const splitCSV = (v) =>
        String(v || "")
          .split(/[,;]+/)
          .map(s => s.trim())
          .filter(Boolean);

      const mapRow = (r) => {
        const obj = {};
        for (const [k, v] of Object.entries(r)) obj[norm(k)] = v;

        // Accept columns: classids/classes/class/classname; sections/sectionids/section; branches/branchids/branch
        const classesArr  = splitCSV(obj.classids || obj.classes || obj.class || obj.classname);
        const sectionsArr = splitCSV(obj.sectionids || obj.sections || obj.section).map(up);
        const branchesArr = splitCSV(obj.branchids || obj.branches || obj.branch).map(up);

        return {
          name: obj.name || "",
          employeeId: obj.employeeid || obj.empid || obj["employee id"] || "",
          primarySubject: obj.primarysubject || obj.subject || "",
          otherSubjects: obj.othersubjects || obj.subjects || "",
          gender: (obj.gender || "").toLowerCase(),
          dob: obj.dob || "",
          phone: obj.phone || obj.mobile || "",
          email: (obj.email || "").toLowerCase(),
          address: obj.address || "",
          joinDate: obj.joindate || obj["join date"] || "",
          status: (obj.status || "active").toLowerCase() === "inactive" ? "inactive" : "active",
          photoURL: obj.photourl || obj["photo url"] || "",

          teacherId: obj.teacherid || obj["teacher id"] || obj.login || obj.username || "",
          password: obj.password || "",

          // New mapping
          classIds: classesArr,
          sectionIds: sectionsArr,
          branchIds: branchesArr,

          // legacy (firsts)
          className: classesArr[0] || "",
          section: sectionsArr[0] || "",
          branch: branchesArr[0] || "",
        };
      };

      const parsed = json.map(mapRow).filter(r => r.name);
      setRows(parsed);
    } catch (e) {
      await Swal.fire({ icon: "error", title: "Could not parse file", text: e?.message || "Try another file." });
    } finally {
      setLoading(false);
    }
  };

  const handleFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!/\.(xlsx?|csv)$/i.test(f.name)) {
      Swal.fire({ icon: "info", title: "Use .xlsx or .csv file" });
      return;
    }
    parseFile(f);
  };

  const startImport = async () => {
    if (!rows.length) return Swal.fire({ icon: "info", title: "No rows to import" });
    onImport({ rows, autoCreateAccounts });
  };

  return (
    <Modal show={show} onHide={onHide} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Bulk Upload Teachers</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="mb-2 text-muted">
          Upload an <b>XLSX/CSV</b> with columns like: <code>name*</code>, <code>employeeId</code>,
          <code>primarySubject</code>, <code>otherSubjects</code>, <code>gender</code>, <code>dob</code>, <code>phone</code>,
          <code>email</code>, <code>address</code>, <code>joinDate</code>, <code>photoURL</code>,
          <code>teacherId</code>, <code>password</code>, <code>classIds / classes</code>, <code>sectionIds / sections</code>, <code>branchIds / branches</code>.
        </div>
        <Form.Group className="mb-3">
          <Form.Control type="file" accept=".xlsx,.xls,.csv" onChange={handleFile} disabled={loading} />
        </Form.Group>

        <Form.Check
          type="switch"
          id="auto-create"
          checked={autoCreateAccounts}
          onChange={(e) => setAutoCreateAccounts(e.target.checked)}
          label="Create login accounts for rows that have an email"
        />

        {loading ? (
          <div className="text-center py-4">
            <Spinner animation="border" size="sm" /> Parsing…
          </div>
        ) : rows.length ? (
          <Card className="border-0 shadow-sm">
            <Card.Header className="bg-white small">
              Preview ({rows.length} rows) – first 10 shown
            </Card.Header>
            <Card.Body className="p-0">
              <div className="table-responsive">
                <Table hover className="mb-0 align-middle">
                  <thead>
                    <tr>
                      <th>Name</th><th>Emp ID</th><th>Teacher ID</th>
                      <th>Subject</th><th>Email</th><th>DOB</th>
                      <th>Classes</th><th>Sections</th><th>Branches</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.slice(0, 10).map((r, i) => (
                      <tr key={i}>
                        <td>{r.name}</td>
                        <td>{r.employeeId}</td>
                        <td>{r.teacherId}</td>
                        <td>{r.primarySubject}</td>
                        <td>{r.email}</td>
                        <td>{r.dob}</td>
                        <td>{(r.classIds || []).join(", ")}</td>
                        <td>{(r.sectionIds || []).join(", ")}</td>
                        <td>{(r.branchIds || []).join(", ")}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </Card.Body>
          </Card>
        ) : null}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onHide}>Close</Button>
        <Button onClick={startImport} disabled={!rows.length}>Import</Button>
      </Modal.Footer>
    </Modal>
  );
}

/* ================= main page ================= */

export default function Teachers() {
  const { school, loading: loadingSchool } = useSchool();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  // filters
  const [qText, setQText] = useState("");
  const [subject, setSubject] = useState("all");

  // modal
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);

  // bulk
  const [showBulk, setShowBulk] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkProgress, setBulkProgress] = useState({ done: 0, total: 0 });

  // classes schema for options { className -> {sections:Set, branches:Set} }
  const [classesSchema, setClassesSchema] = useState({});

  const searchRef = useRef(null);

  // live teachers
  useEffect(() => {
    if (!school?.id) return;
    setLoading(true);
    const col = collection(db, "schools", school.id, "teachers");
    const unsub = onSnapshot(
      query(col, orderBy("createdAt", "desc")),
      (snap) => {
        const list = snap.docs.map((d) => {
          const data = d.data() || {};
          return {
            id: d.id,
            name: data.name || "—",
            employeeId: data.employeeId || "",
            primarySubject: data.primarySubject || "",
            otherSubjects: data.otherSubjects || "",
            gender: data.gender || "",
            dob: toDate(data.dob),
            phone: data.phone || "",
            email: data.email || "",
            address: data.address || "",
            joinDate: toDate(data.joinDate),
            status: data.status || "active",
            photoURL: data.photoURL || "",
            auth: data.auth || null, // { uid, email }
            createdAt: toDate(data.createdAt),

            teacherLoginId: data.teacherLoginId || "",

            // NEW arrays (with legacy fallback)
            classIds: Array.isArray(data.classIds) ? data.classIds : (data.className ? [data.className] : []),
            sectionIds: Array.isArray(data.sectionIds) ? data.sectionIds : (data.section ? [up(data.section)] : []),
            branchIds: Array.isArray(data.branchIds) ? data.branchIds : (data.branch ? [up(data.branch)] : []),

            // keep legacy for display
            className: data.className || "",
            section: data.section || "",
            branch: data.branch || "",
          };
        });
        setRows(list);
        setLoading(false);
      },
      () => setLoading(false)
    );
    return () => unsub();
  }, [school?.id]);

  // Build classes schema from students (class -> sections/branches)
  useEffect(() => {
    if (!school?.id) return;

    const colStudents = collection(db, "schools", school.id, "students");
    const unsub = onSnapshot(query(colStudents, orderBy("className", "asc")), (snap) => {
      const map = new Map(); // className -> {sections:Set, branches:Set}
      snap.docs.forEach(d => {
        const s = d.data() || {};
        const className = String(s.className || "").trim();
        if (!className) return;
        const sec = up(s.section || "");
        const br = up(s.branch || s.branchName || "");
        if (!map.has(className)) map.set(className, { sections: new Set(), branches: new Set() });
        if (sec) map.get(className).sections.add(sec);
        if (br) map.get(className).branches.add(br);
      });
      // Convert to plain object with Sets preserved for runtime
      const obj = {};
      map.forEach((v, k) => obj[k] = v);
      setClassesSchema(obj);
    });

    return () => unsub();
  }, [school?.id]);

  const subjectList = useMemo(() => {
    const s = new Set();
    rows.forEach((r) => r.primarySubject && s.add(r.primarySubject));
    return ["all", ...Array.from(s).sort()];
  }, [rows]);

  const filtered = useMemo(() => {
    const q = qText.trim().toLowerCase();
    return rows.filter((r) => {
      if (subject !== "all" && r.primarySubject !== subject) return false;
      if (q) {
        const hay = [
          r.name, r.employeeId, r.teacherLoginId, r.primarySubject, r.otherSubjects,
          r.email, r.phone, r.address,
          ...(r.classIds || []), ...(r.sectionIds || []), ...(r.branchIds || [])
        ].join(" ").toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [rows, qText, subject]);

  const openCreate = () => { setEditing(null); setShowModal(true); };
  const openEdit = (row) => { setEditing(row); setShowModal(true); };

  // Upload helper
  const uploadAvatar = async (teacherId, file) => {
    if (!file) return null;
    const ext = file.name.split(".").pop()?.toLowerCase() || "jpg";
    const path = `schools/${school.id}/teachers/${teacherId}/avatar_${Date.now()}.${ext}`;
    const storageRef = ref(storage, path);
    await uploadBytes(storageRef, file, { contentType: file.type });
    return getDownloadURL(storageRef);
  };

  // Reset email
  const handleSendReset = async (row) => {
    if (!row?.email) {
      return Swal.fire({ icon: "info", title: "No email on file" });
    }
    try {
      await sendPasswordResetEmail(getAuth(), row.email);
      Swal.fire({
        icon: "success",
        title: "Reset email sent",
        text: `A password reset link was emailed to ${row.email}`,
        timer: 1600,
        showConfirmButton: false,
      });
    } catch (e) {
      Swal.fire({
        icon: "error",
        title: "Could not send reset email",
        text: e?.message || "Try again",
      });
    }
  };

  // Create or update Firestore + Auth (single)
  const save = async (payload) => {
    // Arrays cleaned in modal
    const classIds = Array.isArray(payload.classIds) ? payload.classIds : (payload.className ? [payload.className] : []);
    const sectionIds = Array.isArray(payload.sectionIds) ? payload.sectionIds.map(up) : (payload.section ? [up(payload.section)] : []);
    const branchIds = Array.isArray(payload.branchIds) ? payload.branchIds.map(up) : (payload.branch ? [up(payload.branch)] : []);

    const base = {
      name: payload.name,
      employeeId: payload.employeeId,
      primarySubject: payload.primarySubject,
      otherSubjects: payload.otherSubjects,
      gender: payload.gender,
      dob: payload.dob ? new Date(payload.dob) : null,
      phone: payload.phone,
      email: payload.email || null,
      address: payload.address,
      joinDate: payload.joinDate ? new Date(payload.joinDate) : null,
      status: payload.status || "active",
      updatedAt: serverTimestamp(),

      teacherLoginId: payload.teacherLoginId || "",

      // NEW arrays
      classIds,
      sectionIds,
      branchIds,

      // Legacy singles (firsts)
      className: payload.className || classIds[0] || "",
      section: payload.section || sectionIds[0] || "",
      branch: payload.branch || branchIds[0] || "",
    };

    // Helper to pick password for Auth creation
    const pickPassword = (explicit) => {
      if (explicit && explicit.length >= 6) return explicit;
      return randomPass(10);
    };

    if (editing) {
      await updateDoc(doc(db, "schools", school.id, "teachers", editing.id), {
        ...base,
        ...(payload.removePhoto && !payload.photoFile ? { photoURL: "" } : {}),
      });

      if (payload.photoFile) {
        const url = await uploadAvatar(editing.id, payload.photoFile);
        await updateDoc(doc(db, "schools", school.id, "teachers", editing.id), {
          photoURL: url || "",
          updatedAt: serverTimestamp(),
        });
      }

      // Create auth if not present but email exists
      if (payload.email && !editing.auth?.uid) {
        const chosenPw = pickPassword(payload.accountPassword);
        try {
          const user = await createTeacherAuthUser({
            email: payload.email,
            password: chosenPw,
            displayName: payload.name,
          });

          await Promise.all([
            updateDoc(doc(db, "schools", school.id, "teachers", editing.id), {
              auth: { uid: user.uid, email: user.email },
              updatedAt: serverTimestamp(),
            }),
            setDoc(doc(db, "users", user.uid), {
              role: "teacher",
              schoolId: school.id,
              teacherId: editing.id,
              email: user.email,
              displayName: payload.name || "",
              primarySubject: payload.primarySubject || "",
              status: (payload.status || "active"),
              // mirror mapping for convenience
              classIds, sectionIds, branchIds,
              className: base.className, section: base.section, branch: base.branch,
              createdAt: serverTimestamp(),
              updatedAt: serverTimestamp(),
            }, { merge: true }),
          ]);

          await Swal.fire({
            icon: "success",
            title: "Teacher updated & account created",
            html: `
              <div style="text-align:left;line-height:1.55">
                <p><strong>Email:</strong> ${user.email}</p>
                <p><strong>Password:</strong> <code>${chosenPw}</code></p>
                <p class="text-muted">Share credentials with the teacher; they should change the password after first login.</p>
              </div>
            `,
            showCancelButton: true,
            confirmButtonText: "Copy",
            cancelButtonText: "Close",
            preConfirm: () => {
              const t = `Email: ${user.email}\nPassword: ${chosenPw}`;
              return navigator.clipboard.writeText(t).catch(() => {});
            },
          });
        } catch (e) {
          Swal.fire({ icon: "error", title: "Auth creation failed", text: e?.message || "Try again" });
        }
      } else {
        // also mirror mapping to user if account already exists
        if (editing?.auth?.uid) {
          await setDoc(doc(db, "users", editing.auth.uid), {
            classIds, sectionIds, branchIds,
            className: base.className, section: base.section, branch: base.branch,
            updatedAt: serverTimestamp(),
          }, { merge: true });
        }
        Swal.fire({ icon: "success", title: "Updated", timer: 900, showConfirmButton: false });
      }
    } else {
      const refDoc = await addDoc(collection(db, "schools", school.id, "teachers"), {
        ...base,
        photoURL: "",
        createdAt: serverTimestamp(),
      });

      if (payload.photoFile) {
        const url = await uploadAvatar(refDoc.id, payload.photoFile);
        await updateDoc(refDoc, { photoURL: url || "", updatedAt: serverTimestamp() });
      }

      if (payload.email) {
        const chosenPw = pickPassword(payload.accountPassword);
        try {
          const user = await createTeacherAuthUser({
            email: payload.email,
            password: chosenPw,
            displayName: payload.name,
          });

          await Promise.all([
            updateDoc(refDoc, {
              auth: { uid: user.uid, email: user.email },
              updatedAt: serverTimestamp(),
            }),
            setDoc(doc(db, "users", user.uid), {
              role: "teacher",
              schoolId: school.id,
              teacherId: refDoc.id,
              email: user.email,
              displayName: payload.name || "",
              primarySubject: payload.primarySubject || "",
              status: (payload.status || "active"),
              classIds, sectionIds, branchIds,
              className: base.className, section: base.section, branch: base.branch,
              createdAt: serverTimestamp(),
              updatedAt: serverTimestamp(),
            }, { merge: true }),
          ]);

          await Swal.fire({
            icon: "success",
            title: "Teacher added & account created",
            html: `
              <div style="text-align:left;line-height:1.55">
                <p><strong>Email:</strong> ${user.email}</p>
                <p><strong>Password:</strong> <code>${chosenPw}</code></p>
                <p class="text-muted">Share credentials with the teacher; they should change the password after first login.</p>
              </div>
            `,
            showCancelButton: true,
            confirmButtonText: "Copy",
            cancelButtonText: "Close",
            preConfirm: () => {
              const t = `Email: ${user.email}\nPassword: ${chosenPw}`;
              return navigator.clipboard.writeText(t).catch(() => {});
            },
          });
        } catch (e) {
          Swal.fire({ icon: "error", title: "Auth creation failed", text: e?.message || "Try again" });
        }
      } else {
        Swal.fire({ icon: "success", title: "Added", timer: 900, showConfirmButton: false });
      }
    }
  };

  const removeRow = async (row) => {
    const res = await Swal.fire({
      icon: "warning",
      title: "Delete teacher?",
      text: `Remove "${row.name}" (${row.primarySubject || "No subject"}).`,
      showCancelButton: true,
      confirmButtonColor: "#d33",
      confirmButtonText: "Delete",
    });
    if (!res.isConfirmed) return;
    await deleteDoc(doc(db, "schools", school.id, "teachers", row.id));
    Swal.fire({ icon: "success", title: "Deleted", timer: 900, showConfirmButton: false });
  };

  const exportCSV = () => {
    const data = filtered.map((r) => ({
      id: r.id,
      name: r.name,
      employeeId: r.employeeId,
      teacherLoginId: r.teacherLoginId || "",
      primarySubject: r.primarySubject,
      otherSubjects: r.otherSubjects,
      gender: r.gender,
      dob: fmt(r.dob, "YYYY-MM-DD"),
      phone: r.phone,
      email: r.email,
      address: r.address,
      joinDate: fmt(r.joinDate, "YYYY-MM-DD"),
      status: r.status || "active",
      photoURL: r.photoURL || "",
      createdAt: fmt(r.createdAt, "YYYY-MM-DD"),
      uid: r.auth?.uid || "",
      // arrays
      classIds: (r.classIds || []).join(", "),
      sectionIds: (r.sectionIds || []).join(", "),
      branchIds: (r.branchIds || []).join(", "),
      // legacy
      className: r.className || "",
      section: r.section || "",
      branch: r.branch || "",
    }));
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "teachers");
    XLSX.writeFile(wb, "teachers_export.csv");
  };

  // -------- Bulk import handler --------
  const handleBulkImport = async ({ rows: toImport, autoCreateAccounts }) => {
    if (!school?.id) return;
    setShowBulk(false);
    setBulkBusy(true);
    setBulkProgress({ done: 0, total: toImport.length });

    const creds = []; // {name,email,password}
    let processed = 0;

    const coerceDate = (raw) => {
      if (!raw) return null;
      const s = String(raw).trim();
      let d = null;
      if (/^\d{4}-\d{2}-\d{2}$/.test(s)) d = new Date(s);
      else if (/^\d{2}\/\d{2}\/\d{4}$/.test(s)) {
        const [dd, mm, yyyy] = s.split("/");
        d = new Date(`${yyyy}-${mm}-${dd}`);
      } else if (!Number.isNaN(Date.parse(s))) {
        d = new Date(s);
      }
      return d && !isNaN(d.getTime()) ? d : null;
    };

    for (const r of toImport) {
      try {
        const classIds = Array.isArray(r.classIds) ? r.classIds : (r.className ? [r.className] : []);
        const sectionIds = Array.isArray(r.sectionIds) ? r.sectionIds.map(up) : (r.section ? [up(r.section)] : []);
        const branchIds = Array.isArray(r.branchIds) ? r.branchIds.map(up) : (r.branch ? [up(r.branch)] : []);

        const base = {
          name: r.name,
          employeeId: r.employeeId || "",
          teacherLoginId: r.teacherId || "",
          primarySubject: r.primarySubject || "",
          otherSubjects: r.otherSubjects || "",
          gender: r.gender || "",
          dob: coerceDate(r.dob),
          phone: r.phone || "",
          email: r.email || null,
          address: r.address || "",
          joinDate: coerceDate(r.joinDate),
          status: (r.status || "active") === "inactive" ? "inactive" : "active",
          photoURL: "",
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),

          // mapping
          classIds, sectionIds, branchIds,
          className: classIds[0] || "",
          section: sectionIds[0] || "",
          branch: branchIds[0] || "",
        };

        // 1) create doc
        const refDoc = await addDoc(collection(db, "schools", school.id, "teachers"), base);

        // 2) (Optional) account
        if (autoCreateAccounts && r.email) {
          const password = (r.password && String(r.password).length >= 6) ? String(r.password) : randomPass(10);
          try {
            const user = await createTeacherAuthUser({
              email: r.email,
              password,
              displayName: r.name,
            });

            await Promise.all([
              updateDoc(refDoc, {
                auth: { uid: user.uid, email: user.email },
                updatedAt: serverTimestamp(),
              }),
              setDoc(doc(db, "users", user.uid), {
                role: "teacher",
                schoolId: school.id,
                teacherId: refDoc.id,
                email: user.email,
                displayName: r.name || "",
                primarySubject: r.primarySubject || "",
                status: base.status,
                classIds, sectionIds, branchIds,
                className: base.className, section: base.section, branch: base.branch,
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp(),
              }, { merge: true }),
            ]);

            creds.push({ name: r.name, email: user.email, password });
          } catch (e) {
            // continue with others
          }
        }
      } catch (e) {
        // continue next
      } finally {
        processed += 1;
        setBulkProgress({ done: processed, total: toImport.length });
      }
    }

    setBulkBusy(false);

    if (creds.length) {
      const ws = XLSX.utils.json_to_sheet(creds);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "credentials");
      const blob = XLSX.write(wb, { type: "array", bookType: "xlsx" });
      const url = URL.createObjectURL(new Blob([blob], { type: "application/octet-stream" }));

      const html = `
        <div style="text-align:left;line-height:1.6">
          <p><strong>Created ${creds.length} teacher accounts.</strong></p>
          <div style="max-height:180px;overflow:auto;border:1px solid #eee;border-radius:8px;padding:8px">
            ${creds
              .slice(0, 20)
              .map(c => `<div><code>${c.email}</code> — <b>${c.password}</b> <span style="color:#666">(${c.name})</span></div>`)
              .join("")}
            ${creds.length > 20 ? `<div style="color:#666">…and ${creds.length - 20} more</div>` : ""}
          </div>
          <p class="text-muted" style="margin-top:8px">Download the full list as XLSX.</p>
        </div>
      `;

      const result = await Swal.fire({
        icon: "success",
        title: "Bulk import completed",
        html,
        showDenyButton: true,
        confirmButtonText: "Copy first 10",
        denyButtonText: "Download XLSX",
      });

      if (result.isDenied) {
        const a = document.createElement("a");
        a.href = url;
        a.download = "teacher_credentials.xlsx";
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
      } else {
        const first10 = creds.slice(0, 10).map(c => `${c.email} - ${c.password}`).join("\n");
        navigator.clipboard.writeText(first10).catch(() => {});
      }
    } else {
      Swal.fire({ icon: "success", title: "Bulk import finished" });
    }
  };

  if (loadingSchool) {
    return (
      <Card className="border-0 shadow-sm">
        <Card.Body className="py-5 text-center text-muted">
          <Spinner animation="border" size="sm" /> Loading…
        </Card.Body>
      </Card>
    );
  }

  if (!school?.id) {
    return <div className="text-muted">No school selected.</div>;
  }

  return (
    <Container fluid className="p-0">
      {/* Header */}
      <Row className="mb-3 g-2 align-items-center">
        <Col>
          <h5 className="mb-0">Teachers</h5>
          <small className="text-muted">Manage teacher records for {school.name}</small>
        </Col>
        <Col className="text-end">
          <ButtonGroup className="action-toolbar gap-2">
            <Button
              variant="outline-secondary"
              className="btn-elev px-3 py-2"
              onClick={exportCSV}
              aria-label="Export CSV"
            >
              <i className="bi bi-download me-2" /> Export CSV
            </Button>

            <Button
              variant="outline-primary"
              className="btn-elev px-3 py-2"
              onClick={() => setShowBulk(true)}
              aria-label="Bulk Upload"
            >
              <i className="bi bi-upload me-2" /> Bulk Upload
            </Button>

            <Button
              className="btn-elev px-3 py-2"
              onClick={openCreate}
              aria-label="Add Teacher"
            >
              <i className="bi bi-plus-lg me-2" /> Add Teacher
            </Button>
          </ButtonGroup>
        </Col>
      </Row>

      {/* Bulk progress banner */}
      {bulkBusy && (
        <Card className="border-0 shadow-sm mb-3">
          <Card.Body className="py-3">
            <div className="d-flex align-items-center justify-content-between">
              <div>
                <div className="fw-semibold">Importing teachers…</div>
                <div className="small text-muted">{bulkProgress.done} / {bulkProgress.total}</div>
              </div>
              <div style={{ minWidth: 240 }}>
                <ProgressBar now={bulkProgress.total ? (bulkProgress.done / bulkProgress.total) * 100 : 0} />
              </div>
            </div>
          </Card.Body>
        </Card>
      )}

      <Card className="p-3">
        <Row className="g-2 align-items-end mb-2">
          <Col md={6}>
            <Form.Label className="mb-1">Search</Form.Label>
            <InputGroup>
              <InputGroup.Text><i className="bi bi-search" /></InputGroup.Text>
              <Form.Control
                ref={searchRef}
                placeholder="Name, emp ID, teacher ID, subject, class, section, branch, email, phone…"
                value={qText}
                onChange={(e) => setQText(e.target.value)}
              />
              {qText && (
                <Button variant="outline-secondary" onClick={() => setQText("")}>Clear</Button>
              )}
            </InputGroup>
          </Col>
          <Col md={4}>
            <Form.Label className="mb-1">Primary Subject</Form.Label>
            <Form.Select value={subject} onChange={(e) => setSubject(e.target.value)}>
              {subjectList.map((c) => <option key={c} value={c}>{c === "all" ? "All" : c}</option>)}
            </Form.Select>
          </Col>
          <Col md={2} className="text-end">
            <Badge bg="light" text="dark" className="border">
              {filtered.length} of {rows.length} shown
            </Badge>
          </Col>
        </Row>

        <div className="table-responsive">
       <Card className="border-0 shadow-sm">
  <Card.Header className="bg-body d-flex align-items-center justify-content-between">
    <div className="fw-semibold">Teachers</div>
    {/* room for filters/search on the right if needed */}
  </Card.Header>

  <div className="table-responsive">
    <Table hover className="align-middle table-modern mb-0">
      <thead>
        <tr>
          <th className="d-none d-sm-table-cell sticky-th">Avatar</th>
          <th className="sticky-th">Name</th>
          <th className="d-none d-md-table-cell sticky-th">Emp ID</th>
          <th className="d-none d-md-table-cell sticky-th">Teacher ID</th>
          <th className="sticky-th">Classes / Sections / Branches</th>
          <th className="sticky-th">Primary Subject</th>
          <th className="d-none d-lg-table-cell sticky-th">Other Subjects</th>
          <th className="d-none d-md-table-cell sticky-th">Gender</th>
          <th className="d-none d-lg-table-cell sticky-th">DOB</th>
          <th className="d-none d-lg-table-cell sticky-th">Phone</th>
          <th className="d-none d-xl-table-cell sticky-th">Email</th>
          <th className="d-none d-xl-table-cell sticky-th">Account</th>
          <th className="text-end sticky-th">Actions</th>
        </tr>
      </thead>

      <tbody>
        {loading ? (
          <tr>
            <td colSpan={13} className="text-center py-5">
              <Spinner animation="border" size="sm" /> Loading…
            </td>
          </tr>
        ) : filtered.length === 0 ? (
          <tr>
            <td colSpan={13} className="text-center text-muted py-4">
              No teachers found
            </td>
          </tr>
        ) : (
          filtered.map((t) => {
            const classes = t.classIds || [];
            const sections = t.sectionIds || [];
            const branches = t.branchIds || [];

            const Chip = ({ children, variant = "light", className = "" }) => (
              <Badge bg={variant} text={variant === "light" ? "dark" : undefined} className={`me-1 mb-1 chip ${className}`}>
                {children}
              </Badge>
            );

            const Chips = ({ items = [], max = 3, variant = "light" }) => {
              const shown = items.slice(0, max);
              const rest = items.length - shown.length;
              return (
                <>
                  {shown.map((v) => (
                    <Chip key={v} variant={variant}>{v}</Chip>
                  ))}
                  {rest > 0 && <Chip variant={variant}>+{rest}</Chip>}
                </>
              );
            };

            const avatar = t.photoURL ? (
              <img
                src={t.photoURL}
                alt={t.name}
                className="avatar-ring"
                style={{ height: 36, width: 36, borderRadius: 10, objectFit: "cover" }}
              />
            ) : (
              <div
                className="d-inline-flex justify-content-center align-items-center bg-light border avatar-ring"
                style={{ height: 36, width: 36, borderRadius: 10, fontSize: 12, fontWeight: 600, color: "#666" }}
                title={t.name}
              >
                {initials(t.name)}
              </div>
            );

            return (
              <tr key={t.id} className="row-hover">
                <td className="d-none d-sm-table-cell" style={{ width: 56 }}>{avatar}</td>

                <td style={{ minWidth: 220, maxWidth: 260 }}>
                  <div className="fw-semibold text-truncate">{t.name}</div>
                  <div className="text-muted small text-truncate">
                    {t.employeeId || t.id}
                  </div>
                </td>

                <td className="d-none d-md-table-cell">{t.employeeId || "—"}</td>
                <td className="d-none d-md-table-cell">{t.teacherLoginId || "—"}</td>

                <td style={{ minWidth: 260 }}>
                  <div className="d-flex flex-wrap">
                    <Chips items={classes} max={2} variant="light" />
                  </div>
                  <div className="d-flex flex-wrap">
                    <Chips items={sections} max={3} variant="secondary" />
                  </div>
                  <div className="d-flex flex-wrap">
                    <Chips items={branches} max={3} variant="info" />
                  </div>
                </td>

                <td>{t.primarySubject || "—"}</td>
                <td className="d-none d-lg-table-cell text-truncate" style={{ maxWidth: 220 }}>
                  {t.otherSubjects || "—"}
                </td>
                <td className="d-none d-md-table-cell text-capitalize">{t.gender || "—"}</td>
                <td className="d-none d-lg-table-cell">{fmt(t.dob)}</td>
                <td className="d-none d-lg-table-cell">{t.phone || "—"}</td>
                <td className="d-none d-xl-table-cell text-truncate" style={{ maxWidth: 240 }}>
                  {t.email || "—"}
                </td>

                <td className="d-none d-xl-table-cell">
                  {t.auth?.uid ? (
                    <>
                      <Badge bg={t.status === "inactive" ? "secondary" : "success"} className="me-2">
                        {t.status === "inactive" ? "Inactive" : "Active"}
                      </Badge>
                      <span className="text-muted small">{t.auth.email || t.email}</span>
                    </>
                  ) : (
                    <Badge bg="secondary">No account</Badge>
                  )}
                </td>

                <td className="text-end" style={{ width: 70 }}>
                  <Dropdown align="end">
                    <Dropdown.Toggle variant="outline-secondary" size="sm">
                      <i className="bi bi-three-dots-vertical" />
                    </Dropdown.Toggle>
                    <Dropdown.Menu>
                      {t.email && (
                        <>
                          <Dropdown.Item onClick={() => navigator.clipboard.writeText(t.email).catch(() => {})}>
                            <i className="bi bi-clipboard me-2" />
                            Copy email
                          </Dropdown.Item>
                          <Dropdown.Item onClick={() => handleSendReset(t)}>
                            <i className="bi bi-envelope-paper me-2" />
                            Send reset email
                          </Dropdown.Item>
                          <Dropdown.Divider />
                        </>
                      )}
                      <Dropdown.Item onClick={() => openEdit(t)}>
                        <i className="bi bi-pencil me-2" />
                        Edit
                      </Dropdown.Item>
                      <Dropdown.Item className="text-danger" onClick={() => removeRow(t)}>
                        <i className="bi bi-trash me-2" />
                        Delete
                      </Dropdown.Item>
                    </Dropdown.Menu>
                  </Dropdown>
                </td>
              </tr>
            );
          })
        )}
      </tbody>
    </Table>
  </div>
</Card>

        </div>
      </Card>

      {/* Single Add/Edit modal */}
      <TeacherFormModal
        show={showModal}
        onHide={() => setShowModal(false)}
        initial={editing}
        onSave={save}
        classesSchema={classesSchema}   // pass schema with sections & branches
      />

      {/* Bulk Upload modal */}
      <BulkUploadModal show={showBulk} onHide={() => setShowBulk(false)} onImport={handleBulkImport} />

      <style>{`
        .card { border-radius: 14px; }
        .form-control, .form-select { border-radius: 10px; }
        /* Modern table polish */
.table-modern thead th.sticky-th {
  position: sticky;
  top: 0;
  z-index: 2;
  background: var(--bs-body-bg);
  backdrop-filter: saturate(180%) blur(8px);
  /* subtle divider */
  box-shadow: inset 0 -1px 0 rgba(0,0,0,.06);
}

.table-modern tbody tr.row-hover {
  transition: box-shadow .18s ease, transform .08s ease, background-color .18s ease;
}
.table-modern tbody tr.row-hover:hover {
  background-color: var(--bs-secondary-bg);
}

/* tiny rounded chips */
.table-modern .chip {
  border-radius: 999px;
  padding: .25rem .5rem;
  font-weight: 600;
}

/* avatar ring */
.avatar-ring {
  box-shadow: 0 0 0 2px var(--bs-body-bg), 0 0 0 3px rgba(0,0,0,.06);
}
  /* Overall elevation + rounded modal */
.modal-elevated .modal-content {
  border: 0;
  border-radius: 1rem;
  box-shadow: 0 10px 30px rgba(0,0,0,.18);
}

/* Section framing */
.section { padding: .5rem 0 0; }
.section + .section { border-top: 1px solid var(--bs-border-color); margin-top: 1rem; padding-top: 1rem; }
.section-title { font-weight: 600; opacity: .9; margin-bottom: .5rem; display: flex; align-items: center; }

/* Subtle helper text */
.form-hint { font-size: .8rem; color: var(--bs-secondary-color); margin-top: .25rem; }

/* Avatar panel (drag & drop) */
.avatar-panel { border: 1px dashed var(--bs-border-color); background: rgba(0,0,0,.02); transition: background .2s ease, border-color .2s ease; }
.avatar-panel.drag { background: rgba(0,0,0,.04); border-color: var(--bs-primary); }
.avatar-lg { width: 104px; height: 104px; object-fit: cover; border-radius: 50%; border: 1px solid var(--bs-border-color); box-shadow: 0 0 0 3px var(--bs-body-bg); }
.avatar-lg.placeholder { display: inline-flex; align-items: center; justify-content: center; width: 104px; height: 104px; border-radius: 50%; border: 1px dashed var(--bs-border-color); color: var(--bs-secondary-color); font-size: 2.25rem; }

/* Sticky footer action bar */
.sticky-footer { position: sticky; bottom: 0; background: var(--bs-body-bg); box-shadow: 0 -6px 16px rgba(0,0,0,.06); }

/* Dark mode friendliness (Bootstrap 5.3 uses CSS variables; this keeps contrast nice) */
[data-bs-theme="dark"] .avatar-panel { background: rgba(255,255,255,.02); }
[data-bs-theme="dark"] .avatar-panel.drag { background: rgba(255,255,255,.06); }

      `}</style>
    </Container>
  );
}
